import { CSSProperties, ReactNode, useEffect, useState } from 'react';

import { ModalBase } from '~src/components/Modal/common';

type Background = 'white' | 'blur' | 'blurWhite';

type OverlayModalProps = {
  background?: Background;
  children?: ReactNode;
  visible?: boolean;
  setVisible?: (visible: boolean) => void;
  showTime?: number;
  fadeOut?: boolean;
  fadeIn?: boolean;
  style?: CSSProperties;
};

export const OverlayModal = ({
  background,
  children,
  visible = true,
  setVisible,
  showTime,
  fadeOut,
  fadeIn,
  style,
}: OverlayModalProps) => {
  const [fadeState, setFadeState] = useState<'in' | 'out'>();

  const maskStyle = getBackground(background);
  const fadeOutStyle = fadeOut && fadeState === 'out' ? { opacity: 0 } : {};
  const fadeInStyle = fadeIn && fadeState === 'in' ? { opacity: 1 } : {};

  const fadeStyle = { transition: 'opacity 0.5s', opacity: fadeIn ? 0 : 1, ...fadeInStyle, ...fadeOutStyle };

  useEffect(() => {
    setFadeState('in');
    if (showTime) {
      const timeout = setTimeout(() => setVisible?.(false), showTime);
      const timeoutForFade = setTimeout(() => setFadeState?.('out'), showTime - 500);
      return () => {
        clearTimeout(timeout);
        clearTimeout(timeoutForFade);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ModalBase
      {...{
        visible,
        destroyOnClose: true,
        style: { visibility: 'hidden', ...fadeStyle },
        maskStyle: { ...maskStyle, visibility: 'visible', ...fadeStyle },
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          visibility: 'visible',
          ...style,
        }}
      >
        {children}
      </div>
    </ModalBase>
  );
};

const getBackground = (background?: Background) => {
  switch (background) {
    case 'blur': {
      return { backdropFilter: 'blur(5px)' };
    }
    case 'blurWhite': {
      return { background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(5px)' };
    }
    case 'white': {
      return { background: 'white' };
    }
    default: {
      return;
    }
  }
};
